/**
 * Pure proration helpers extracted from useSubscription for unit testing.
 * No React, no Supabase — only date math and rounding rules.
 */
export type PlanType = 'free' | 'pro' | 'business';
export type BillingPeriod = 'monthly' | 'annual';

export interface ProrationInput {
  currentPlan: PlanType;
  billingPeriod: BillingPeriod;
  expiresAt: string | null;
  startedAt: string | null;
  amountPaid: number;
  newPlan: PlanType;
  newPeriod: BillingPeriod;
  newPrice: number;
  /** Override "now" for deterministic tests. Defaults to new Date(). */
  now?: Date;
}

export interface ProrationResult {
  credit: number;
  amountDue: number;
  finalAmount: number;
}

const DAY_MS = 1000 * 60 * 60 * 24;

function computeCreditFromRemaining(
  amountPaid: number,
  startedAt: string | null,
  expiresAt: string,
  now: Date,
): number {
  const expires = new Date(expiresAt);
  const started = startedAt
    ? new Date(startedAt)
    : new Date(expires.getTime() - 30 * DAY_MS);
  const totalDays = Math.max(1, Math.ceil((expires.getTime() - started.getTime()) / DAY_MS));
  const remainingDays = Math.max(0, Math.ceil((expires.getTime() - now.getTime()) / DAY_MS));
  return Math.round((amountPaid * remainingDays / totalDays) * 100) / 100;
}

export function calculateProration(input: ProrationInput): ProrationResult {
  const {
    currentPlan,
    billingPeriod,
    expiresAt,
    startedAt,
    amountPaid,
    newPlan,
    newPeriod,
    newPrice,
    now = new Date(),
  } = input;

  if (newPlan === 'free') return { credit: 0, amountDue: 0, finalAmount: 0 };

  let credit = 0;

  const sameMonthlyToAnnual =
    currentPlan === newPlan &&
    billingPeriod === 'monthly' &&
    newPeriod === 'annual';

  const upgradeBetweenPlans =
    currentPlan !== 'free' && currentPlan !== newPlan;

  if (
    (sameMonthlyToAnnual || upgradeBetweenPlans) &&
    expiresAt &&
    amountPaid > 0
  ) {
    credit = computeCreditFromRemaining(amountPaid, startedAt, expiresAt, now);
  }

  const finalAmount = Math.max(0, Math.round((newPrice - credit) * 100) / 100);
  return { credit, amountDue: newPrice, finalAmount };
}
